import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const loginOnly = process.env.REACT_APP_PUBLIC_LOGIN_ONLY === "true";
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/admin/dashboard";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            // Use full-page nav to avoid React context race with ProtectedRoute
            window.location.assign(decodeURIComponent(next));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
            setBusy(false);
        }
    };

    const quick = (em) => {
        setEmail(em);
        setPassword("hotelos123");
    };

    return (
        <div className="max-w-md mx-auto px-4 py-16">
            <div className="flex justify-center mb-6">
                <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground grid place-items-center">
                        <Building2 className="w-5 h-5" />
                    </div>
                    <div className="text-lg font-semibold tracking-tight">HotelOS</div>
                </div>
            </div>
            <Card>
                <CardHeader>
                    <h1 className="text-xl font-semibold leading-none tracking-tight">Sign in</h1>
                </CardHeader>
                <CardContent>
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <Label htmlFor="login-email">Email</Label>
                            <Input
                                id="login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="username"
                                required
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="login-password">Password</Label>
                            <Input
                                id="login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                required
                                data-testid="login-password"
                            />
                        </div>
                        <Button type="submit" disabled={busy} className="w-full" data-testid="login-submit">
                            <LogIn className="w-4 h-4 mr-1" /> {busy ? "Signing in…" : "Sign in"}
                        </Button>
                    </form>
                    {!loginOnly && (
                        <div className="text-xs text-muted-foreground mt-4 text-center">
                            New here? <Link to="/register" className="text-foreground hover:underline">Create an account</Link>
                        </div>
                    )}
                </CardContent>
            </Card>
            {!loginOnly && <Card className="mt-4" data-testid="login-test-creds">
                <CardContent className="py-4">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                        Demo accounts (password: <span className="font-mono">hotelos123</span>)
                    </div>
                    <div className="grid grid-cols-1 gap-1.5 text-sm">
                        {[
                            ["admin@hotelos.local", "Owner — full access"],
                            ["manager@hotelos.local", "Manager — full access"],
                            ["frontdesk@hotelos.local", "Front desk"],
                            ["housekeeping@hotelos.local", "Housekeeping"],
                            ["maintenance@hotelos.local", "Maintenance"],
                        ].map(([em, label]) => (
                            <button
                                key={em}
                                onClick={() => quick(em)}
                                className="flex items-center justify-between text-left rounded-md px-2 py-1.5 hover:bg-secondary transition-colors"
                                data-testid={`login-quick-${em.split("@")[0]}`}
                            >
                                <span className="font-mono text-xs">{em}</span>
                                <span className="text-xs text-muted-foreground">{label}</span>
                            </button>
                        ))}
                    </div>
                </CardContent>
            </Card>}
        </div>
    );
}
